import { isAxiosError } from 'axios';
import { router, useLocalSearchParams } from 'expo-router';
import * as ScreenOrientation from 'expo-screen-orientation';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import WebView from 'react-native-webview';

import { useAuthenticateEpisode } from '@/api/hooks/showHooks';
import DotsLoader from '@/components/DotsLoader';
import { APPSFLYER_CONFIG, MAX_RETRIES } from '@/constants/config';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { AppStorage } from '@/utils/AppStorage';
import { encryptObject } from '@/utils/crypto';
import { clearEpisodeProgress, getEpisodeProgress, saveEpisodeProgress } from '@/utils/showEpisodesUtils';

const Episode = () => {
  const params = useLocalSearchParams();
  const episodeId = typeof params.episodeId === 'string' ? params.episodeId : undefined;

  const { showError } = useEndFlowModal();

  const url = APPSFLYER_CONFIG.af_web_dp;

  const { mutateAsync: authenticateEpisode, isPending: authenticatingEpisode, failureCount } = useAuthenticateEpisode();

  const [playerUrl, setPlayerUrl] = useState('');

  const openWebBrowserForAndroid = async (url: string) => {
    const result = await WebBrowser.openBrowserAsync(url);

    // Go back once the user open the episode
    if (result.type === 'opened') {
      router.back();
    }
  };

  const onAuthenticate = async () => {
    while (failureCount < MAX_RETRIES) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const lastTimeStamp = await getEpisodeProgress(episodeId ?? '');

        // eslint-disable-next-line no-await-in-loop
        const data = await authenticateEpisode({
          episodeId: episodeId ?? ''
        });

        // eslint-disable-next-line no-await-in-loop
        const encrypted = await encryptObject({
          playbackId: data.playbackId,
          token: data.token,
          drmToken: data.drmToken,
          lastTimeStamp: lastTimeStamp ?? undefined
        });

        const deepLinkValue = new URLSearchParams({
          deep_link_value: `muxPlayer?payload=${encodeURIComponent(encrypted)}`
        });

        if (Platform.OS === 'android' && data.drmToken) {
          openWebBrowserForAndroid(`${url}?${deepLinkValue}`);
          return;
        }
        setPlayerUrl(`${url}?${deepLinkValue}`);
        return;
      } catch (e) {
        if (isAxiosError(e) && (e.response?.status === 409 || e.status === 409)) {
          continue;
        }

        showError({});
        console.error('Unknown error:', e);
        break;
      }
    }
  };

  useEffect(() => {
    const changeScreenOrientation = async () => {
      await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.LANDSCAPE_LEFT);
    };

    Platform.OS === 'ios' && changeScreenOrientation();

    return () => {
      // Reset to default orientation on unmount
      Platform.OS === 'ios' && ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.DEFAULT);
    };
  }, []);

  useEffect(() => {
    onAuthenticate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeId]);

  const safeParse = (raw: string) => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  return (
    <SafeAreaView edges={['bottom']} style={styles.flexOne}>
      <View style={styles.container}>
        {playerUrl.length === 0 || playerUrl === undefined || authenticatingEpisode ? (
          <DotsLoader />
        ) : (
          <WebView
            style={styles.mainContainer}
            containerStyle={styles.mainContainer}
            injectedJavaScript={`
            (function() {
            document.documentElement.style.backgroundColor = "black";
            document.documentElement.style.height = "100%";
            document.documentElement.style.margin = "0";
            document.documentElement.style.padding = "0";
            document.body.style.backgroundColor = "black";
            document.body.style.height = "100%";
            document.body.style.margin = "0";
            document.body.style.padding = "0";
            document.body.style.overflow = "hidden";
            })();
            `}
            bounces={false}
            showsVerticalScrollIndicator={false}
            startInLoadingState
            renderLoading={() => (
              <View style={styles.mainContainer}>
                <DotsLoader color="white" />
              </View>
            )}
            source={{ uri: playerUrl }}
            contentMode="mobile"
            allowsInlineMediaPlayback
            allowsFullscreenVideo
            allowsAirPlayForMediaPlayback
            mediaPlaybackRequiresUserAction={false}
            javaScriptEnabled
            domStorageEnabled
            webkitShowPlaybackTargetPicker
            onMessage={(event) => {
              const data = safeParse(event.nativeEvent.data);
              if (
                !data ||
                typeof data !== 'object' ||
                !['mux-token-expired', 'save-timestamp', 'episode-ended'].includes(data.type)
              )
                return;

              if (data.type === '$$router_link') {
                return;
              }

              switch (data.type) {
                case '$$router_link':
                  return;

                case 'mux-token-expired':
                  onAuthenticate();
                  break;

                case 'save-timestamp':
                  if (episodeId) saveEpisodeProgress(episodeId, parseFloat(data?.currentTime?.toString() ?? '0'));
                  break;

                case 'episode-ended':
                  if (episodeId) {
                    clearEpisodeProgress(episodeId);
                    AppStorage.setItem('lastEpisodeCompleted', episodeId);
                  }
                  break;

                default:
                  break;
              }
            }}
          />
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  mainContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'black',
    overflow: 'hidden'
  },

  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 25,
    paddingHorizontal: 18,
    width: '100%',
    height: '45%',
    gap: 15
  }
});

export default Episode;
